/**
 * File validation utilities for document uploads
 * Implements validation rules for:
 * - Allowed file types (configurable from backend)
 * - Maximum file size (configurable from backend)
 * - Maximum number of files (configurable from backend)
 */

export interface FileValidationError {
  file?: string;
  reason: string;
}

export interface FileValidationResult {
  valid: boolean;
  errors: FileValidationError[];
}

/**
 * Default allowed file extensions (used if backend settings unavailable)
 */
export const DEFAULT_ALLOWED_FILE_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'txt',
  'json',
  'csv',
  'xlsx',
  'jpg',
  'jpeg',
  'png',
  'pptx'
];

export const MIME_TYPE_MAP: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx'
  ],
  'text/plain': ['.txt'],
  'application/json': ['.json'],
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx'
  ],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
    '.pptx'
  ],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png']
};

/**
 * Default maximum allowed file size in MB (fetched from backend at runtime)
 */
export const DEFAULT_MAX_FILE_SIZE_MB = 25;

/**
 * Default maximum number of files (fetched from backend at runtime)
 */
export const DEFAULT_MAX_FILES_COUNT = 5;

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  return filename.substring(filename.lastIndexOf('.')).toLowerCase();
};

/**
 * Check if a file extension is allowed
 */
export const isAllowedFileType = (
  filename: string,
  allowedExtensions: string[] = DEFAULT_ALLOWED_FILE_EXTENSIONS
): boolean => {
  const extension = getFileExtension(filename);
  return allowedExtensions.some((ext) => `.${ext}` === extension);
};

/**
 * Check if file size is within limits
 */
export const isFileSizeValid = (
  fileSizeBytes: number,
  maxSizeMB: number = DEFAULT_MAX_FILE_SIZE_MB
): boolean => {
  return fileSizeBytes <= maxSizeMB * 1024 * 1024;
};

/**
 * Check if number of files is within limits
 */
export const isFileCountValid = (
  count: number,
  maxCount: number = DEFAULT_MAX_FILES_COUNT
): boolean => {
  return count <= maxCount;
};

/**
 * Validate a single file
 */
export const validateSingleFile = (
  file: File,
  maxSizeMB: number = DEFAULT_MAX_FILE_SIZE_MB,
  allowedExtensions: string[] = DEFAULT_ALLOWED_FILE_EXTENSIONS
): FileValidationResult => {
  const errors: FileValidationError[] = [];

  // Check file type
  if (!isAllowedFileType(file.name, allowedExtensions)) {
    errors.push({
      file: file.name,
      reason: `File type not allowed. Allowed types: ${allowedExtensions.join(', ')}`
    });
  }

  // Check file size
  if (!isFileSizeValid(file.size, maxSizeMB)) {
    errors.push({
      file: file.name,
      reason: `File size is too large. Maximum allowed individual file size is ${maxSizeMB} MB.`
    });
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Validate multiple files
 */
export const validateMultipleFiles = (
  files: File[],
  maxFilesCount: number = DEFAULT_MAX_FILES_COUNT,
  maxSizeMB: number = DEFAULT_MAX_FILE_SIZE_MB,
  allowedExtensions: string[] = DEFAULT_ALLOWED_FILE_EXTENSIONS
): FileValidationResult => {
  const errors: FileValidationError[] = [];

  // Check number of files
  if (!isFileCountValid(files.length, maxFilesCount)) {
    errors.push({
      reason: `Maximum ${maxFilesCount} files allowed. You provided ${files.length} files.`
    });
    return {
      valid: false,
      errors
    };
  }

  // Validate each file
  files.forEach((file) => {
    const singleFileResult = validateSingleFile(file, maxSizeMB, allowedExtensions);
    if (!singleFileResult.valid) {
      errors.push(...singleFileResult.errors);
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Get formatted error message for display to user
 */
export const getFormattedErrorMessage = (validationResult: FileValidationResult): string => {
  if (validationResult.valid) {
    return '';
  }

  if (validationResult.errors.length === 1) {
    return validationResult.errors[0].reason;
  }

  // Group errors by reason
  const errorsByReason = validationResult.errors.reduce(
    (acc, error) => {
      if (!acc[error.reason]) {
        acc[error.reason] = [];
      }
      if (error.file) {
        acc[error.reason].push(error.file);
      }
      return acc;
    },
    {} as Record<string, string[]>
  );

  return Object.entries(errorsByReason)
    .map(([reason, files]) => {
      if (files.length === 0) {
        return reason;
      }
      return `${reason} (${files.join(', ')})`;
    })
    .join('\n');
};
